import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { trigger, transition, style, animate } from '@angular/animations';
import { DataPointCardContentComponent } from './data-point-card-content/data-point-card-content.component';
import { TimeSeriesCardContentComponent } from './time-series-card-content/time-series-card-content.component';
import { SubcatGraphStyle } from '../../../services/result-data/result-data.service';
import { TitleCardContentComponent } from './title-card-content/title-card-content.component';
import { PieChartCardContentComponent } from './pie-chart-card-content/pie-chart-card-content.component';
import { FormatNumbersTextPipe } from '../../../pipes/format-numbers-text.pipe';
import { MatButtonModule } from '@angular/material/button';

export type DataPointCardType = 'single' | 'double';

interface BaseCardConfig {
  transitionId: string;
  title: string;
  categoryName: string;
  colorIndex: number;
  cardIndex: number;
  accent: boolean;
  type: 'dataPoint' | 'timeSeries' | 'pieChart' | 'title';
  colspan: number;
  rowspan?: number;
}

export interface DataPointCardConfig extends BaseCardConfig {
  data: DataPointCardData[];
  dataPointCardType: DataPointCardType;
  info?: string;
  imgFileName?: string;
}

export interface DataPointCardData {
  label: string;
  value: number;
  unit?: string;
  addPieChart?: boolean;
}

export interface TimeSeriesCardConfig extends BaseCardConfig {
  chartStyle: SubcatGraphStyle;
  labels: string[];
  datasets: TimeSeriesCardDataset[];
  unit?: string;
  info?: string;
}

export interface TimeSeriesCardDataset {
  label: string;
  values: number[];
  variant: 'primary' | 'breakdown';
}

export interface TitleCardConfig extends BaseCardConfig {
  imgFileName?: string;
}

export interface PieChartCardSlice {
  label: string;
  value: number;
  rawValue: number;
}

export interface PieChartCardConfig extends BaseCardConfig {
  slices: PieChartCardSlice[];
  total: number;
  unit?: string;
  info?: string;
  yearLabel: string;
  timeHorizonYears?: number;
  subtitle?: string;
}

export type CardConfig =
  | DataPointCardConfig
  | TimeSeriesCardConfig
  | TitleCardConfig
  | PieChartCardConfig;

@Component({
  selector: 'app-card',
  imports: [
    MatIconModule,
    MatTooltipModule,
    DataPointCardContentComponent,
    TimeSeriesCardContentComponent,
    PieChartCardContentComponent,
    TitleCardContentComponent,
    FormatNumbersTextPipe,
    MatButtonModule,
  ],
  templateUrl: './card.component.html',
  styleUrl: './card.component.scss',
  animations: [
    trigger('cardAnim', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(24px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'none' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'none' }),
        animate(
          '300ms ease-in',
          style({ opacity: 0, transform: 'translateY(24px)' })
        ),
      ]),
    ]),
    trigger('hideMenuAnim', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'scaleY(0)',
          transformOrigin: '50% 50%',
        }),
        animate(
          '300ms ease-out',
          style({
            opacity: 1,
            transform: 'scaleY(1)',
            transformOrigin: '50% 50%',
          })
        ),
      ]),
      transition(':leave', [
        style({
          opacity: 1,
          transform: 'scaleY(1)',
          transformOrigin: '50% 50%',
        }),
        animate(
          '300ms ease-in',
          style({
            opacity: 0,
            transform: 'scaleY(0)',
            transformOrigin: '50% 50%',
          })
        ),
      ]),
    ]),
  ],
})
export class CardComponent implements OnInit {
  @Input() config!: BaseCardConfig;
  @Input() hideable = false;

  @HostBinding('style.grid-column') gridColumnSpan = 'span 10';
  @HostBinding('style.grid-row') gridRowSpan = 'span 2';

  @Output() hideCard$ = new EventEmitter();

  hideMenuActive = false;

  showTooltip(event: MouseEvent, tooltip: MatTooltip) {
    event.stopPropagation();
    tooltip.show();
  }

  ngOnInit(): void {
    this._initializeCardLayout();
  }

  isDataPointCard(config: BaseCardConfig): config is DataPointCardConfig {
    return config.type === 'dataPoint';
  }

  isTimeSeriesCard(config: BaseCardConfig): config is TimeSeriesCardConfig {
    return config.type === 'timeSeries';
  }

  isTitleCard(config: BaseCardConfig): config is TitleCardConfig {
    return config.type === 'title';
  }

  isPieChartCard(config: BaseCardConfig): config is PieChartCardConfig {
    return config.type === 'pieChart';
  }

  onClickCard() {
    if (!this.hideable) return;
    this.hideMenuActive = true;
  }

  onClickHide() {
    this.hideCard$.emit();
  }

  onClickReturnCard() {
    this.hideMenuActive = false;
  }

  private _initializeCardLayout() {
    const columnSpan = this._resolveColumnSpan(this.config);
    const clampedSpan = Math.min(Math.max(columnSpan, 1), 10);

    this.gridColumnSpan = `span ${clampedSpan}`;

    switch (this.config.type) {
      case 'dataPoint':
        this.gridRowSpan = 'span 1';
        break;
      case 'title': {
        const rowSpan = this._resolveRowSpan(this.config);
        const clampedRowSpan = Math.min(Math.max(rowSpan, 1), 10);
        this.gridRowSpan = `span ${clampedRowSpan}`;
        break;
      }
      case 'timeSeries':
      case 'pieChart':
      default:
        this.gridRowSpan = 'span 2';
        break;
    }
  }

  private _resolveColumnSpan(config: BaseCardConfig): number {
    return config.colspan && config.colspan > 0 ? config.colspan : 1;
  }

  private _resolveRowSpan(config: BaseCardConfig): number {
    return config.rowspan && config.rowspan > 0 ? config.rowspan : 2;
  }

}
